/**
 * PORT-008 §5.3 — l'en-tête d'un espace : qui je suis, et chez qui je suis.
 *
 * ⚠️ **NON fixe** (pas de `sticky`) : sur un téléphone tenu à une main, un
 * en-tête collant mange le tiers haut de l'écran pendant qu'on lit une liste.
 *
 * ⚠️ Le sélecteur de contexte n'apparaît **qu'à partir de deux espaces**. À un
 * seul, il n'offre aucun choix : ce serait un menu déroulant qui ne déroule
 * rien, et il ferait croire qu'il en existe d'autres.
 */

import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ChevronDown, LogOut, Menu, UserRound } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { PortalContext, PortalUser } from '@/hooks/usePortalMe.types'
import { libelleDeLEspaceEnCours, ligneDeuxDuContexte } from '@/components/portal/contextKind'
import { LigneDeuxDeLEspace } from '@/components/portal/LigneDeuxDeLEspace'
import { PortalContextSwitcher } from '@/components/portal/PortalContextSwitcher'
import { useIsMobile } from '@/hooks/use-mobile'
import { nomDEnTete } from './nomDeLaPersonne'

interface PortalHeaderProps {
    user: PortalUser
    contextes: PortalContext[]
    contexteActif: PortalContext | null
    onOuvrirLeMenu: () => void
    onChoisirLeContexte: (grantId: string) => Promise<boolean>
    onDeconnexion: () => void
}

export function PortalHeader({
    user,
    contextes,
    contexteActif,
    onOuvrirLeMenu,
    onChoisirLeContexte,
    onDeconnexion,
}: PortalHeaderProps) {
    const { t } = useTranslation()
    const plusieurs = contextes.length > 1
    /**
     * 🔴 Le MÊME décideur que la coque, et surtout pas une classe `md:hidden`.
     *
     * En PAYSAGE téléphone (844 × 390), `useIsMobile()` est vrai — la coque
     * retire donc la barre latérale — mais la largeur vaut 844 px, si bien
     * qu'une classe `md:hidden` masquerait AUSSI le hamburger : plus de barre,
     * plus de bouton, **aucune navigation**. Les deux décisions doivent sortir
     * du même endroit, sinon elles divergent exactement là où l'intervenant
     * tourne son téléphone entre deux salles.
     */
    const estMobile = useIsMobile() === true

    return (
        <header className="flex items-center gap-2 border-b px-3 pl-[max(0.75rem,env(safe-area-inset-left))] pr-[max(0.75rem,env(safe-area-inset-right))] pt-[calc(0.75rem+env(safe-area-inset-top))] pb-3 md:px-6 md:pl-[max(1.5rem,env(safe-area-inset-left))] md:pr-[max(1.5rem,env(safe-area-inset-right))]">
            {estMobile && (
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={onOuvrirLeMenu}
                    aria-label={t('portal.nav.openMenu')}
                >
                    <Menu className="h-5 w-5" />
                </Button>
            )}

            <div className="min-w-0 flex-1">
                {plusieurs ? (
                    <PortalContextSwitcher
                        contextes={contextes}
                        contexteActif={contexteActif}
                        onChoisir={onChoisirLeContexte}
                    />
                ) : (
                    <div className="truncate px-2 font-medium">
                        {/* A18 — à zéro espace, la phrase du corps, jamais « Aucun espace actif ». */}
                        {libelleDeLEspaceEnCours(contexteActif, contextes.length, t)}
                    </div>
                )}
                {/* La ligne 2 AFFICHÉE, régime d'une structure compris — la même fonction
                    que la page de choix et le sélecteur (arbitrage n° 11). À un seul
                    espace, c'est la seule surface où une structure lit son régime. Jamais
                    coupée (E8, 08/10) : elle dit dans QUEL magasin d'une enseigne on est entré. */}
                {contexteActif && ligneDeuxDuContexte(contexteActif, t) && (
                    <LigneDeuxDeLEspace texte={ligneDeuxDuContexte(contexteActif, t)} className="px-2" />
                )}
            </div>

            {/*
                ⭐ **Le menu de compte du wireframe** — `PORT-008 §5.3` écrit
                `│ [≡] {logo} Espace client ▾ [C]│` et précise « `[Claire ▾]` =
                Mon compte (→ `/espace/app/compte`) · Se déconnecter ».

                Ce coin ne portait qu'un `<span>` INERTE, masqué sous 640 px : au
                téléphone, l'en-tête n'avait donc qu'une seule porte au lieu de
                deux, et la déconnexion n'existait qu'au fond du tiroir. La revue
                mobile n'avait vu que la moitié visible du défaut — le nom caché —
                et pas la moitié structurelle.

                ⭐ Le nom suit `getDisplayName` d'Argalis Core, via `nomDEnTete`
                (« Julie FONTAINE », « Julie F. », « Julie ») — il affichait
                « dany.varlet » jusqu'au 2026-09-15.
                ⚠️ Jamais `user.email` : le serveur n'en
                sert aucun (`usePortalMe.types.ts` : « Aucune adresse e-mail »),
                et un correctif proposé par la revue ne compilait pas pour cette
                raison exacte.
            */}
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        className="min-h-11 max-w-[10rem] shrink-0 justify-end gap-1 px-2 md:min-h-0 md:h-auto md:max-w-[14rem]"
                    >
                        <UserRound className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                        {/* ⚠️ Le rôle du bouton en `sr-only`, qui S'AJOUTE au nom
                            visible — jamais un `aria-label`, qui le REMPLAÇAIT :
                            « Claire Martin » à l'écran, « Mon compte » pour le
                            lecteur d'écran, et une commande vocale « cliquer
                            Claire Martin » qui ne trouvait rien (WCAG 2.5.3). */}
                        <span className="sr-only">{t('portal.nav.account')}</span>
                        {/* Le nom est MASQUÉ sous 640 px (`hidden sm:inline`) : au
                            téléphone l'en-tête porte déjà le hamburger et le
                            sélecteur d'espace, et c'est l'icône + le chevron qui
                            disent que ce coin s'ouvre. */}
                        <span className="hidden truncate text-sm text-muted-foreground sm:inline">
                            {nomDEnTete(user)}
                        </span>
                        <ChevronDown className="h-4 w-4 shrink-0 opacity-60" aria-hidden />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="max-w-[min(16rem,90vw)]">
                    <DropdownMenuItem asChild className="min-h-11 md:min-h-0">
                        <Link to="/espace/app/compte">
                            <UserRound className="mr-2 h-4 w-4 shrink-0" aria-hidden />
                            {t('portal.nav.account')}
                        </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={onDeconnexion} className="min-h-11 md:min-h-0">
                        <LogOut className="mr-2 h-4 w-4 shrink-0" aria-hidden />
                        {t('portal.nav.logout')}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </header>
    )
}
